import type { QualityCheck, SeasonStep, Totals } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { getWorkspaceSettingsQueryOptions, useWorkspaceDashboard, workspaceSearch } from "@api/generated/workspace/workspace";
import { Icon } from "@app/icons";
import { stepRoute } from "@app/seasonCycle";
import { useRecent, useSeason } from "@app/workspace";
import { isActiveJob, JobFileButton, JobStatusChip, useMyJobs } from "@features/system/jobs";
import { QualityRow } from "@features/system/quality";
import { Button, Kbd, Loader, Progress, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { SeasonChip } from "@shell/SeasonChip";
import { hitTarget } from "@shell/targets";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { formatNumber } from "@ui/format";
import { RecentRow } from "@ui/StartCard";
import type { TFunction } from "i18next";
import { type FormEvent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

const HOME_REDIRECT_DONE = "breeder.home";

export const Route = createFileRoute("/")({
  // the start screen of Beállítások → Kezdőképernyő, once per browser session (later visits of "/" stay here)
  beforeLoad: async ({ context }) => {
    if (sessionStorage.getItem(HOME_REDIRECT_DONE)) return;
    sessionStorage.setItem(HOME_REDIRECT_DONE, "1");
    const settings = await context.queryClient.ensureQueryData(getWorkspaceSettingsQueryOptions()).catch(() => undefined);
    if (settings && settings.home_page !== "/") throw redirect({ href: settings.home_page });
  },
  component: Home,
});

function Home() {
  const { t } = useTranslation();
  const me = useUserMe();
  const { ev } = useSeason();
  const dash = useWorkspaceDashboard({ ev: ev ?? 0 }, { query: { enabled: !!ev, staleTime: 60_000 } });
  const name = me.data?.full_name ?? me.data?.username ?? "";

  return (
    <div className="page">
      <div className="hstack" style={{ alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div className="grow">
          <h1 className="h1">{t("home.welcome", { name: name })}</h1>
          {ev && (
            <p className="mu hstack" style={{ margin: "4px 0 0", fontSize: "var(--b-text-heading)", gap: 8 }}>
              <SeasonChip />
              {t("home.subtitle", { ev, next: ev + 1 })}
            </p>
          )}
        </div>
        <ScanForm ev={ev} />
      </div>

      {dash.isPending && <Loader size="sm" />}
      {dash.data && <SeasonCycle steps={dash.data.steps} ev={dash.data.ev} />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
        <RecentCard />
        <JobsCard />
        <AttentionCard checks={dash.data?.attention ?? []} totals={dash.data?.totals} />
      </div>

      <div className="hstack" style={{ gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <span className="sm mu">
          <Trans i18nKey="home.tip" components={{ k: <Kbd /> }} />
        </span>
      </div>
    </div>
  );
}

function ScanForm({ ev }: { ev: number | undefined }) {
  const { t } = useTranslation();
  const router = useRouter();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ev || !code.trim()) return;
    setBusy(true);
    try {
      const [plot] = await workspaceSearch({ q: code.trim(), ev, kind: "plot" });
      const target = plot ? hitTarget(plot) : null;
      if (target) void router.navigate(target);
      else notifications.show({ color: "yellow", message: t("home.notPlotCode") });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="hstack" style={{ gap: 8 }} onSubmit={submit} aria-label={t("home.barcode")}>
      <label htmlFor="scan" className="sm mu">
        {t("home.barcode")}
      </label>
      <TextInput
        id="scan"
        value={code}
        onChange={(e) => setCode(e.currentTarget.value)}
        placeholder={t("home.barcodePlaceholder")}
        w={240}
        styles={{ input: { fontFamily: "var(--b-mono)" } }}
      />
      <Button type="submit" loading={busy}>
        {t("home.open")}
      </Button>
    </form>
  );
}

function stepDetail(t: TFunction, s: SeasonStep): string {
  if (s.total) return t(`home.steps.${s.key}.count`, { count: s.count, total: s.total });
  return s.count > 0 ? t(`home.steps.${s.key}.count`, { count: formatNumber(s.count) }) : t(`home.steps.${s.key}.empty`);
}

const STEP_STYLE = {
  done: { background: "var(--b-primary-soft)" },
  active: { background: "var(--b-surface)", border: "2px solid var(--b-primary)" },
  todo: { background: "var(--b-surface-2)", border: "1px dashed var(--b-border-strong)" },
};
const STEP_LABEL_COLOR = { done: "var(--b-primary-ink)", active: "var(--b-primary)", todo: undefined };

function SeasonCycle({ steps, ev }: { steps: SeasonStep[]; ev: number }) {
  const { t } = useTranslation();
  const title = t("home.cycle", { ev, next: ev + 1 });
  return (
    <section className="card pad" style={{ gap: 14 }} aria-label={title}>
      <div className="hstack" style={{ gap: 10 }}>
        <h2 className="h2">{title}</h2>
        <span className="chip g">{t("home.stepsDone", { done: steps.filter((s) => s.state === "done").length, total: steps.length })}</span>
      </div>
      <ol
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 8,
        }}
      >
        {steps.map((s, i) => {
          const to = stepRoute(s.key);
          const name = t(`home.steps.${s.key}.title`);
          const body = (
            <>
              <span className="sec" style={{ color: STEP_LABEL_COLOR[s.state] }}>
                {i + 1} · {t(`home.state.${s.state}`)}
              </span>
              <span className="hstack" style={{ fontWeight: 600, justifyContent: "space-between", gap: 6 }}>
                {name}
                {to && <Icon name="chevronRight" size={14} />}
              </span>
              <span className="sm mu">{stepDetail(t, s)}</span>
              {s.progress != null && s.state !== "done" && <Progress value={s.progress} size="xs" />}
            </>
          );
          return (
            <li key={s.key} style={{ display: "flex" }}>
              {to ? (
                <Link to={to} className="cycle-step" style={STEP_STYLE[s.state]}>
                  {body}
                </Link>
              ) : (
                <div className="cycle-step" style={STEP_STYLE[s.state]}>
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function RecentCard() {
  const { t } = useTranslation();
  const { recent } = useRecent();
  return (
    <section className="card pad">
      <div className="hstack">
        <h2 className="h2">{t("home.resume")}</h2>
        <span className="sm mu grow" style={{ marginLeft: 8 }} title={t("home.yearIndependent")}>
          {t("home.yearIndependentShort")}
        </span>
        <span className="sm mu">{t("home.recentWork")}</span>
      </div>
      {recent.length === 0 && <span className="sm mu">{t("home.noRecent")}</span>}
      {recent.slice(0, 5).map((r) => (
        <RecentRow key={r.href} item={r} showKind />
      ))}
    </section>
  );
}

function JobsCard() {
  const { t } = useTranslation();
  const jobs = (useMyJobs().data ?? []).slice(0, 4);
  return (
    <section className="card pad" style={{ gap: 12 }}>
      <div className="hstack">
        <h2 className="h2">{t("home.jobs")}</h2>
        <span className="sm mu grow" style={{ marginLeft: 8 }} title={t("home.yearIndependent")}>
          {t("home.yearIndependentShort")}
        </span>
        <Link to="/system/jobs" className="sm">
          {t("home.all")}
        </Link>
      </div>
      {jobs.length === 0 && <span className="sm mu">{t("home.noJobs")}</span>}
      {jobs.map((j) =>
        isActiveJob(j) ? (
          <div key={j.id} className="stack" style={{ gap: 6 }}>
            <div className="hstack" style={{ gap: 8 }}>
              <b style={{ fontWeight: 600, fontSize: "0.8438rem", flexGrow: 1 }}>{j.title}</b>
              <span className="sm mu">{j.progress}%</span>
            </div>
            <Progress value={j.progress} size="sm" />
            {j.message && <span className="sm mu">{j.message}</span>}
          </div>
        ) : (
          <div key={j.id} className="hstack" style={{ gap: 10 }}>
            <JobStatusChip status={j.status} />
            <span className="grow" style={{ fontSize: "var(--b-text-body)" }}>
              {j.title}
            </span>
            <JobFileButton job={j} />
          </div>
        ),
      )}
    </section>
  );
}

function AttentionCard({ checks, totals }: { checks: QualityCheck[]; totals?: Totals }) {
  const { t } = useTranslation();
  return (
    <section className="card pad">
      <div className="hstack">
        <h2 className="h2 grow">{t("home.attention")}</h2>
        <Link to="/system/journal" search={{ tab: "quality" }} className="sm">
          {t("home.dataQuality")}
        </Link>
      </div>
      {checks.length === 0 && <span className="sm mu">{t("home.allClear")}</span>}
      {[
        {
          key: "ev",
          items: checks.filter((c) => c.scope_year != null),
          title: t("home.attentionYear", { ev: checks.find((c) => c.scope_year != null)?.scope_year }),
        },
        { key: "registers", items: checks.filter((c) => c.scope_year == null), title: t("home.attentionRegisters") },
      ]
        .filter((g) => g.items.length)
        .map((g) => (
          <div key={g.key} className="stack" style={{ gap: 6 }}>
            <span className="sec">{g.title}</span>
            {g.items.map((c) => (
              <QualityRow key={c.key} check={c} tinted />
            ))}
          </div>
        ))}
      <div style={{ flexGrow: 1 }} />
      {totals && (
        <div className="hstack" style={{ gap: 16, paddingTop: 10, borderTop: "1px solid var(--b-row)" }}>
          <div className="stack">
            <span className="sec">{t("home.years")}</span>
            <b className="mono" style={{ fontSize: "1.125rem" }}>
              {totals.first_year === totals.last_year ? totals.first_year : `${totals.first_year}–${totals.last_year}`}
            </b>
          </div>
          <div className="stack">
            <span className="sec">{t("home.rows")}</span>
            <b className="mono" style={{ fontSize: "1.125rem" }}>
              {formatNumber(totals.rows)}
            </b>
          </div>
        </div>
      )}
    </section>
  );
}
